<template>
  <div :class="[fieldClass, fieldName]">
    <div :id="fieldName"></div>
    <label
      :for="fieldName"
      class="floating req"
      :class="[{ 'is-active': isActive }]"
      @click="onFocus"
    >{{fieldLabel}}</label>
    <span
      v-if="errors.length > 0 && showErrors"
      class="field-validation-error"
    >
      {{getValidationForField(errors[0])}}
    </span>
    <slot/>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

const validationsMap = {
  'is required': 'required',
  'is not a valid card number': 'validCardNumber',
  'is not a valid expiration date': 'validCardExpirationDate',
  'is not a valid security code': 'validCardSecurityCode'
}
/**
 * Renders a VGS iframe input with the custom html needed to keep normal form styling
 * Needs to be mounted from inside a vgs-form
 */
export default {
  name: 'vgs-input',
  props: {
    /**
     * Class to be added to the parent div
     */
    fieldClass: {
      type: String,
      default: ''
    },
    /**
     * Name of the field, it is used as id for the iframe wrapper
     */
    fieldName: {
      type: String,
      required: true
    },
    /**
     * Floating label for the field
     */
    fieldLabel: {
      type: String,
      required: true
    },
    /**
     * VGS collect form instance for generating the input
     */
    form: {
      type: Object,
      required: true
    },
    /**
     * Object for customizing the css, check: [vgs collect customization](https://www.verygoodsecurity.com/docs/vgs-collect/js/customization)
     */
    css: {
      type: Object,
      default: () => ({})
    },
    /**
     * Configuration options for input check: [vgs docs](https://www.verygoodsecurity.com/docs/vgs-collect/js/integration)
     */
    options: {
      type: Object,
      required: true
    },
    /**
     * Tracks if the form have been submitted once before
     */
    haveBeenSubmited: Boolean
  },
  data: () => ({
    /**
     * @vuese
     * Store the VGS input object
     */
    input: null,
    /**
     * @vuese
     * Tracks input focus for floating label
     */
    inFocus: false,
    /**
     * @vuese
     * Tracks input blur error state
     */
    isTouched: false,
    /**
     * @vuese
     * Tracks input errors
     */
    errors: []
  }),
  computed: {
    ...mapGetters('i18n', ['tc']),
    isActive() {
      return this.inFocus || this.errors[0] !== 'is required'
    },
    showErrors() {
      return this.isTouched || this.haveBeenSubmited
    }
  },
  watch: {
    /**
     * @vuese
     * After submission we clean the touched state
     */
    haveBeenSubmited(val) {
      if (!val) {
        this.isTouched = false
      }
    }
  },
  mounted() {
    this.input = this.form.field(`#${this.fieldName}`, {
      ...this.options,
      css: this.css
    })
    this.input.on('focus', () => this.onFocus())
    this.input.on('blur', () => this.onBlur())
    this.input.on('update', (fieldState) => this.errors = fieldState.errorMessages)
  },
  methods: {
    onFocus() {
      this.inFocus = true
      this.$emit('fire-vgs-event')
    },
    onBlur() {
      this.isTouched = true
      this.inFocus = false
      this.$emit('fire-vgs-event')
    },
    /**
     * @vuese
     * Get the translated validation error message for the field
     * @arg Single string with the validation name
     * @param {string} validation
     */
    getValidationForField(validation) {
      return this.tc(`validations.${validationsMap[validation]}`).replace('{0}', this.fieldLabel)
    },
  }
}
</script>
